// SupplierView.jsx (updated with API integration)
import React, { useState, useEffect } from "react";
import {
  FaUser,
  FaBuilding,
  FaPhone,
  FaEnvelope,
  FaCrown,
  FaCheckCircle,
  FaInfoCircle,
  FaShoppingCart,
  FaMoneyBillWave,
  FaHome,
  FaPaw,
  FaUsers,
  FaFileInvoiceDollar,
  FaClock,
  FaMapMarkerAlt,
  FaGlobe,
  FaTimes,
  FaFileInvoice, // Add this for credit note icon
} from "react-icons/fa";
import ThemeMaker from "../../components/thememaker";
import { useNavigate, useParams } from "react-router-dom";
import { useSidebar } from "../../context/SidebarContext";
import { httpService } from "../../services/httpService";
import PurchaseBill from "./PurchaseBill";
import SupplierOverview from "./SupplierOverview";
import SupplierProduct from "./SupplierProduct";
import SupplierPayment from "./SupplierPayment";
import SupplierExpiry from "./Supplierexpiry";
import SupplierCreditNote from "./SupplierCreditNote"; // Create this component

const TABS = [
  {
    key: "overview",
    label: "Overview",
  },
  {
    key: "productList",
    label: "Product/Inventory",
  },
  {
    key: "purchaseBill",
    label: "Purchase Bill",
  },
  {
    key: "return",
    label: "Expiry/Return",
  },
  {
    key: "Payments",
    label: "Payments",
  },
  {
    key: "creditNote",  // New tab
    label: "Credit Note",
  },
];

const SupplierView = () => {
  const { setShowSidebar } = useSidebar();
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("vendorActiveTab") || "overview";
  });

  useEffect(() => {
    setShowSidebar(true);
  }, [setShowSidebar]);

  // Fetch supplier data
  useEffect(() => {
    if (!supplierId) return;
    
    const fetchSupplier = async () => {
      try {
        setLoading(true);
        const response = await httpService.get(
          `/vendorsupplier/getVendorSupplier/${supplierId}`
        );
        
        if (response.status && response.code === 200) {
          setSupplier(response.data);
        }
      } catch (error) {
        console.error("Error fetching supplier:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSupplier();
  }, [supplierId]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return <SupplierOverview hideHeader={true} />;
      case "purchaseBill":
        return <PurchaseBill supplierId={supplierId} />;
      case "productList":
        return <SupplierProduct />;
      case "return":
        return <SupplierExpiry />;
      case "Payments":
        return <SupplierPayment />;
      case "creditNote":
        return <SupplierCreditNote supplierId={supplierId} />;
      default:
        return <div style={{ padding: 40 }}>Content</div>;
    }
  };

  return (
    <ThemeMaker>
      <div className="w-screen h-screen max-w-full px-4 relative flex flex-col justify-start overflow-hidden overflow-x-hidden">
        <div className="flex flex-col md:flex-row gap-4 w-full max-w-full">
          <div className="flex-1 w-full max-w-full">
            {/* Tabs */}
            <div
              className="flex space-x-2 md:space-x-8 rounded-t-md px-6 py-4 w-full bg-white overflow-x-auto"
              style={{
                boxShadow: "0 2px 12px 0 rgba(80,80,200,0.08)",
                minHeight: "60px",
              }}
            >
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  className={`relative px-6 py-2 text-lg font-semibold focus:outline-none flex items-center whitespace-nowrap
                    ${
                      activeTab === tab.key
                        ? "text-white bg-gradient-to-r from-blue-500 to-purple-500 rounded-md shadow font-bold"
                        : "text-gray-700 hover:text-blue-600"
                    }`}
                  style={
                    activeTab === tab.key
                      ? { boxShadow: "0 2px 12px 0 rgba(80,80,200,0.15)" }
                      : {}
                  }
                  onClick={() => {
                    setActiveTab(tab.key);
                    localStorage.setItem("vendorActiveTab", tab.key);
                  }}
                >
                  {tab.label === "Credit Note" }
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Fixed Supplier Info Card - Below Tabs, Above Content */}
            {!loading && supplier && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-md p-4 mt-4 mb-2 border border-blue-100">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  {/* Left side - Basic Info */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center shadow-md">
                        <FaUser className="text-white text-md" />
                      </div>

                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        
                        {/* Name + Phone */}
                        <h2 className="text-xl font-bold text-gray-800">
                          {supplier?.name || "Supplier Name"}{" "}
                          <span className="text-sm font-normal text-blue-800">
                            ({supplier?.contact_info?.phone || "N/A"})
                          </span>
                        </h2>

                        <span className="text-gray-300">|</span>

                        {/* Email */}
                        <div className="flex items-center gap-1 text-gray-600">
                          <FaEnvelope className="text-blue-500 text-xs" />
                          <span>{supplier?.contact_info?.email || "Not provided"}</span>
                        </div>

                        <span className="text-gray-300">|</span>

                        {/* GST */}
                        <div className="flex items-center gap-1 text-gray-600">
                          <FaBuilding className="text-purple-500 text-xs" />
                          <span>GST: {supplier?.gst_number || "Not provided"}</span>
                        </div>

                        <span className="text-gray-300">|</span>

                        {/* PAN */}
                        <div className="flex items-center gap-1 text-gray-600">
                          <FaFileInvoiceDollar className="text-orange-500 text-xs" />
                          <span>PAN: {supplier?.pan_number || "Not provided"}</span>
                        </div>

                      </div>
                    </div>
                </div>
              </div>
            )}

            {/* Loading state for supplier info */}
            {loading && (
              <div className="bg-gray-50 rounded-xl shadow-md p-5 mt-4 mb-6 border border-gray-200 animate-pulse">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gray-300 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-5 bg-gray-300 rounded w-48 mb-2"></div>
                    <div className="h-4 bg-gray-300 rounded w-32"></div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Content */}
            <div className="min-h-[300px] w-full">
              {loading ? (
                <div className="text-center text-gray-500 mt-12">
                  Loading...
                </div>
              ) : supplier ? (
                renderTabContent()
              ) : (
                <div className="text-center text-gray-500 mt-12">
                  No details found.
                </div>
              )}
            </div>
            
            {/* Back Button Below Tabs */}
            <div className="px-6 py-4 bg-white border-t mt-4">
              <button
                onClick={() => navigate("/admin/inventory-management/suppliers")}
                className="flex items-center px-4 py-2 border border-gray-300 bg-gray-100 
               rounded-md text-gray-700 hover:bg-gray-200 hover:border-gray-400 
               font-medium shadow-sm transition"
              >
                Back   
              </button>
            </div>
          </div>
        </div>
      </div>
    </ThemeMaker>
  );
};

export default SupplierView;